// Plan de Emergencias v2 (PE-A1): tipos comunes de las calculadoras.
// Diseño en docs/diseno/plan_emergencias_v2/PASO0_DISENO.md §3.

export type EstadoTraza = 'cumple' | 'no_cumple' | 'informativo' | 'por_validar';

// Cada resultado lleva su trazabilidad: de qué norma y de qué línea sale cada umbral.
export interface Traza {
  metodo: string;
  norma: string;
  articulo_o_tabla: string;
  umbral: number | string | null;
  valor: number | string | null;
  unidad: string;
  estado: EstadoTraza;
  fuente_doc: string;
  nota?: string;
  paso?: number; // paso del asistente donde se completa lo que falta (trazas 'por_validar'; PE-A3b)
}

// Un valor con la fuente de donde sale (archivo:línea de docs/normativa o sección del PASO0).
export interface ValorConFuente<T> {
  valor: T;
  norma: string;
  articulo_o_tabla: string;
  fuente_doc: string;
  estado: 'verificado' | 'transcrito_de_imagen' | 'por_validar';
  pe?: string;
}

// F3a2: lo que se ve en pantalla y en los PDF es la fuente PÚBLICA (nombre de la norma y dónde se publicó), nunca una
// ruta del repo. La línea de docs/normativa sigue como argumento: es la referencia interna, solo en el código.
export const FUENTE_PUBLICA = {
  am1257: 'Acuerdo Ministerial 1257 (MIES, 2009), Reglamento de Prevención, Mitigación y Protección contra Incendios',
  rd164: 'Real Decreto 164/2025 (España), RSCIEI, BOE-A-2025-7190 (referencia técnica)',
  rtq: (n: number) => `Regla Técnica Metropolitana RTQ ${n}/2026, Cuerpo de Bomberos de Quito (Resolución ADMQ 017-2026)`,
  criterio: 'Criterio técnico de StorSafe (por validar)',
} as const;

// Línea de docs/normativa/plan_emergencias/MATRIZ_NORMATIVA.md → norma pública de esa fila (las filas sin norma son
// métodos técnicos: criterio de StorSafe).
const MATRIZ_PUBLICA: Record<number, string> = {
  20: FUENTE_PUBLICA.am1257, 33: 'Método MESERI (1985); criterio técnico de StorSafe (por validar)', 39: FUENTE_PUBLICA.am1257,
  47: FUENTE_PUBLICA.rtq(3), 49: FUENTE_PUBLICA.rtq(3), 53: 'Método de Togawa (RSET); criterio técnico de StorSafe (por validar)',
  59: FUENTE_PUBLICA.rtq(5), 62: FUENTE_PUBLICA.rtq(5), 63: FUENTE_PUBLICA.rtq(5), 74: FUENTE_PUBLICA.am1257, 80: FUENTE_PUBLICA.rtq(5),
  88: FUENTE_PUBLICA.rtq(7), 92: FUENTE_PUBLICA.am1257, 100: FUENTE_PUBLICA.am1257, 126: FUENTE_PUBLICA.rtq(3), 133: FUENTE_PUBLICA.am1257,
  142: FUENTE_PUBLICA.criterio, 143: 'Decreto Ejecutivo 255 (2024), Anexo 1, PO-16',
  145: 'Matriz amenaza × vulnerabilidad 4 × 4 (NEC-SE-DS; IGEPN); criterio técnico de StorSafe (por validar)',
};
const primeraLinea = (l: number | string) => Number(String(l).match(/\d+/)?.[0] ?? NaN);

export const doc = {
  am1257: (_linea: number | string) => FUENTE_PUBLICA.am1257,
  rd164: (_linea: number | string) => FUENTE_PUBLICA.rd164,
  rtq: (n: number, _linea: number | string) => FUENTE_PUBLICA.rtq(n),
  matriz: (linea: number | string) => MATRIZ_PUBLICA[primeraLinea(linea)] ?? FUENTE_PUBLICA.criterio,
  paso0: (_seccion: string) => FUENTE_PUBLICA.criterio,
};

/** Une varias fuentes con " · " sin repetir: si una ya dice "criterio técnico de StorSafe", no se agrega otra igual. */
export function unirFuentes(...fuentes: string[]): string {
  const unicas = Array.from(new Set(fuentes.filter(Boolean)));
  const conCriterio = unicas.some(f => f !== FUENTE_PUBLICA.criterio && /criterio técnico de StorSafe/i.test(f));
  return unicas.filter(f => !(conCriterio && f === FUENTE_PUBLICA.criterio)).join(' · ');
}
